import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateArtworkFromActivity, generateImage } from '../lib/openai';
import MapOverlay from './MapOverlay';

const { FiRefreshCw, FiDownload, FiShare2, FiImage, FiMap, FiToggleRight, FiToggleLeft } = FiIcons;

const ArtworkGenerator = ({ activity, onShare }) => {
  const [loading, setLoading] = useState(false);
  const [artwork, setArtwork] = useState(null);
  const [image, setImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [mapOpacity, setMapOpacity] = useState(0.7);
  const [error, setError] = useState(null);

  const handleGenerateArtwork = async () => {
    if (!activity) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await generateArtworkFromActivity(activity);
      setArtwork(result);
    } catch (err) {
      console.error('Error generating artwork:', err);
      setError('Failed to generate artwork. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!artwork) return;
    
    setImageLoading(true);
    setError(null);
    
    try {
      const result = await generateImage(artwork.imagePrompt);
      setImage(result);
    } catch (err) {
      console.error('Error generating image:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setImageLoading(false);
    }
  };

  const handleDownload = () => {
    if (!image || !image.url) {
      setError('No image available to download');
      return;
    }
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `tempo-art-${activity.id || Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = () => {
    if (onShare && artwork && image) {
      onShare(artwork, image);
    } else if (!image) {
      setError('Please generate an image first');
    }
  };

  const handleMapOpacityChange = (e) => {
    setMapOpacity(parseFloat(e.target.value));
  };

  if (error) {
    return (
      <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6">
        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6 text-center">
          <p className="text-red-300">{error}</p>
        </div>
        <div className="flex justify-center">
          <motion.button 
            onClick={() => setError(null)}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Try Again
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <SafeIcon icon={FiImage} className="w-5 h-5 text-orange-400" />
        AI Artwork Generator
      </h2>

      {!artwork && !loading && (
        <motion.div 
          className="flex flex-col items-center justify-center p-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <p className="text-gray-300 mb-6">
            Transform your activity into a unique piece of art with AI.
            Generate a poetic interpretation and visual artwork based on your performance data.
          </p>
          
          <motion.button
            onClick={handleGenerateArtwork}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Generate Artwork
          </motion.button>
        </motion.div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="mb-4"
          >
            <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-orange-400" />
          </motion.div>
          <p className="text-gray-300">Analyzing activity data and generating artwork...</p>
        </div>
      )}

      <AnimatePresence>
        {artwork && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-2">{artwork.title}</h3>
              <p className="text-gray-300 italic">{artwork.poem}</p>
            </div>

            <div className="bg-black/20 rounded-xl p-5 mb-6">
              <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Artwork Analysis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Theme</p>
                  <p className="text-white">{artwork.artwork.theme}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Style</p>
                  <p className="text-white">{artwork.artwork.style}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Poetic Influence</p>
                  <p className="text-white">{artwork.artwork.poeticInfluence}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Color Palette</p>
                  <p className="text-white">{artwork.artwork.palette}</p>
                </div>
              </div>
            </div>

            {!image && (
              <motion.div 
                className="flex justify-center mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <motion.button
                  onClick={handleGenerateImage}
                  disabled={imageLoading}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-medium rounded-xl transition-all duration-300 disabled:opacity-50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {imageLoading ? (
                    <>
                      <SafeIcon icon={FiRefreshCw} className="w-5 h-5 animate-spin" />
                      Generating Image...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiImage} className="w-5 h-5" />
                      Generate Visual Art
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}

            {image && (
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative overflow-hidden rounded-xl aspect-square mb-4">
                  <img 
                    src={image.url} 
                    alt={artwork.title} 
                    className="object-cover w-full h-full"
                  />
                  
                  {/* Map Overlay */}
                  {showMapOverlay && (
                    <div className="absolute inset-0" style={{ opacity: mapOpacity }}>
                      <MapOverlay activity={activity} />
                    </div>
                  )}
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-white text-lg font-bold">{artwork.title}</h3>
                  </div>
                </div>

                {/* Map Overlay Controls */}
                <div className="flex items-center justify-between mb-4 bg-black/30 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setShowMapOverlay(!showMapOverlay)} 
                      className="flex items-center gap-2 text-white"
                    >
                      <SafeIcon 
                        icon={showMapOverlay ? FiToggleRight : FiToggleLeft} 
                        className={`w-10 h-6 ${showMapOverlay ? 'text-orange-400' : 'text-gray-400'}`} 
                      />
                      <span className="text-sm">GPS Route Overlay</span>
                    </button>
                  </div>
                  
                  {showMapOverlay && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white">Opacity:</span>
                      <input 
                        type="range" 
                        min="0.1" 
                        max="1" 
                        step="0.1" 
                        value={mapOpacity} 
                        onChange={handleMapOpacityChange}
                        className="w-24 accent-orange-500" 
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-center gap-4">
                  <motion.button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-5 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    Download
                  </motion.button>
                  
                  <motion.button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                    Share to Instagram
                  </motion.button>
                </div>
              </motion.div>
            )}

            <div className="flex justify-center mt-6">
              <motion.button
                onClick={handleGenerateArtwork}
                className="flex items-center gap-2 px-4 py-2 text-orange-400 hover:text-orange-300 font-medium transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                Generate New Artwork
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtworkGenerator;